// TwiWorldHome.jsx
// Front-end only "TwiWorld"
// Place in src/components/ and import: `import TwiWorldHome from './components/TwiWorldHome'` then use <TwiWorldHome />
// Features:
// - Virtual village map (grid of plots) where players can buy plots and place crops/decorations
// - Marketplace: create listings, buy listings (local simulated economy)
// - Avatar customization (name, hat color)
// - Trade history / activity feed
// - Seasonal events and daily bonus
// - LocalStorage persistence (no backend required)
// - Responsive layout for mobile and desktop

import React, { useEffect, useMemo, useState } from 'react';
import { Line } from "react-chartjs-2";
import "chart.js/auto";

// Dummy IoT data generator
const generateIoTData = () => ({
  temperature: (20 + Math.random() * 10).toFixed(2),
  humidity: (40 + Math.random() * 20).toFixed(2),
  pressure: (1010 + Math.random() * 10).toFixed(2),
  status: Math.random() > 0.9 ? "ALERT" : "NORMAL",
  timestamp: new Date(),
});

const MAP_ROWS = 6;
const MAP_COLS = 8;
const START_COINS = 500;
const PLOT_BASE_PRICE = 120;
const ITEMS = [
  { id: 'wheat_seed', name: 'Wheat Seed', price: 12, icon: '🌾' },
  { id: 'corn_seed', name: 'Corn Seed', price: 18, icon: '🌽' },
  { id: 'tomato_seed', name: 'Tomato Seed', price: 14, icon: '🍅' },
  { id: 'fence', name: 'Fence', price: 80, icon: '🪵' },
  { id: 'tree', name: 'Fruit Tree', price: 140, icon: '🌳' }
];

function lsGet(key, fallback){ try{ const v = localStorage.getItem(key); return v? JSON.parse(v): fallback; }catch(e){ return fallback; } }
function lsSet(key, val){ try{ localStorage.setItem(key, JSON.stringify(val)); }catch(e){} }

function makeEmptyMap(){
  const grid = Array.from({length: MAP_ROWS}).map(()=> Array.from({length: MAP_COLS}).map(()=> ({ owner:null, content:null })));
  return grid;
}

// Dummy cameras setup
const initialCameras = [
  { id: 1, name: "Camera A", lat: 28.6139, long: 77.209 },
  { id: 2, name: "Camera B", lat: 19.076, long: 72.8777 },
  { id: 3, name: "Camera C", lat: 12.9716, long: 77.5946 },
  { id: 4, name: "Camera D", lat: 13.0827, long: 80.2707 },
];

// Dummy image gallery
const initialImages = [
  {
    url: "https://via.placeholder.com/400x250.png?text=Captured+Image+1",
    lat: 28.6139,
    long: 77.209,
    timestamp: new Date(),
  },
  {
    url: "https://via.placeholder.com/400x250.png?text=Captured+Image+2",
    lat: 19.076,
    long: 72.8777,
    timestamp: new Date(),
  },
];

export default function TwiWorldHome(){
  const [player, setPlayer] = useState(() => lsGet('ag_player', {id: 'player-'+Math.floor(Math.random()*9999), name: 'Farmer', hat:'#2e7d32'}));
  const [coins, setCoins] = useState(() => lsGet('ag_coins', START_COINS));
  const [map, setMap] = useState(() => lsGet('ag_map', makeEmptyMap()));
  const [selectedPlot, setSelectedPlot] = useState(null); // {r,c}
  const [market, setMarket] = useState(() => lsGet('ag_market', []));
  const [feed, setFeed] = useState(() => lsGet('ag_feed', []));
  const [inventory, setInventory] = useState(() => lsGet('ag_inv', {wheat_seed:2,corn_seed:1}));
  const [seasonIndex, setSeasonIndex] = useState(() => lsGet('ag_season', 0));
  const seasons = ['Spring','Summer','Monsoon','Winter'];
  const [helpVisible, setHelpVisible] = useState(false);

  const [cameraFeeds, setCameraFeeds] = useState(
      initialCameras.map((cam) => ({ ...cam, iotHistory: [generateIoTData()], streaming: false }))
    );
    const [images, setImages] = useState(initialImages);
  
    // Simulate IoT feed per camera
    useEffect(() => {
      const interval = setInterval(() => {
        setCameraFeeds((prev) =>
          prev.map((cam) => {
            const newIot = generateIoTData();
            return {
              ...cam,
              iotHistory: [...cam.iotHistory.slice(-19), newIot],
            };
          })
        );
      }, 2000);
      return () => clearInterval(interval);
    }, []);
  
    // Capture new image
    const captureNewImage = (cameraId) => {
      const camera = cameraFeeds.find((c) => c.id === cameraId);
      const newImage = {
        cameraId: camera.id,
        url: `https://via.placeholder.com/400x250.png?text=Captured+Image+${images.length + 1}`,
        lat: camera.lat + Math.random() * 0.01,
        long: camera.long + Math.random() * 0.01,
        timestamp: new Date(),
      };
      setImages([newImage, ...images]);
    };
  
    // Toggle streaming
    const toggleStreaming = (cameraId) => {
      setCameraFeeds((prev) =>
        prev.map((cam) =>
          cam.id === cameraId ? { ...cam, streaming: !cam.streaming } : cam
        )
      );
    };
  
    // Helper: chart config per camera
    const getChartData = (iotHistory) => ({
      labels: iotHistory.map((d) => d.timestamp.toLocaleTimeString("en-US", { hour12: false })),
      datasets: [
        {
          label: "Temperature (°C)",
          data: iotHistory.map((d) => d.temperature),
          borderColor: "rgba(255,99,132,1)",
          backgroundColor: "rgba(255,99,132,0.2)",
          tension: 0.3,
        },
        {
          label: "Humidity (%)",
          data: iotHistory.map((d) => d.humidity),
          borderColor: "rgba(54,162,235,1)",
          backgroundColor: "rgba(54,162,235,0.2)",
          tension: 0.3,
        },
        {
          label: "Pressure (hPa)",
          data: iotHistory.map((d) => d.pressure),
          borderColor: "rgba(255,206,86,1)",
          backgroundColor: "rgba(255,206,86,0.2)",
          tension: 0.3,
        },
      ],
    });
  
    const chartOptions = {
      responsive: true,
      plugins: { legend: { position: "top", labels: { color: "#f0f0f0" } } },
      animation: { duration: 500 },
      scales: {
        y: { beginAtZero: false, ticks: { color: "#f0f0f0" } },
        x: { ticks: { color: "#f0f0f0" } },
      },
    };

  // persist
  useEffect(()=> lsSet('ag_player', player), [player]);
  useEffect(()=> lsSet('ag_coins', coins), [coins]);
  useEffect(()=> lsSet('ag_map', map), [map]);
  useEffect(()=> lsSet('ag_market', market), [market]);
  useEffect(()=> lsSet('ag_feed', feed), [feed]);
  useEffect(()=> lsSet('ag_inv', inventory), [inventory]);
  useEffect(()=> lsSet('ag_season', seasonIndex), [seasonIndex]);

  const seasonName = seasons[seasonIndex % seasons.length];

  // helpers
  function announce(text){ const entry = { ts: Date.now(), text }; setFeed(f => [entry, ...f].slice(0,80)); }

  function buyPlot(r,c){
    const plot = map[r][c]; if(plot.owner) return; const price = PLOT_BASE_PRICE + (r+c)*10;
    if(coins < price){ announce('Not enough coins to buy this plot.'); return; }
    setCoins(cn => cn - price);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].owner = player.id; nm[r][c].content = { type: 'empty' }; return nm; });
    announce(`${player.name} bought plot (${r+1},${c+1}) for ₹${price}`);
  }

  function placeItem(r,c, itemId){
    const item = ITEMS.find(it => it.id===itemId); if(!item) return; if(coins < item.price){ announce('Not enough coins to buy item.'); return; }
    setCoins(cn => cn - item.price);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type: 'item', id: itemId, placedBy: player.id }; return nm; });
    announce(`${player.name} placed ${item.name} on plot (${r+1},${c+1})`);
  }

  function plantCrop(r,c, seedId){
    const seed = ITEMS.find(it => it.id===seedId); if(!seed) return; if(!inventory[seedId] || inventory[seedId] <=0){ announce('No seeds in inventory. Buy from market.'); return; }
    setInventory(inv => ({...inv, [seedId]: Math.max(0, (inv[seedId]||0)-1)}));
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type:'crop', id: seedId, plantedOn: Date.now(), growth: 0 }; return nm; });
    announce(`${player.name} planted ${seed.name} at (${r+1},${c+1})`);
  }

  function harvest(r,c){ const cell = map[r][c]; if(!cell || !cell.content) return; if(cell.content.type !== 'crop') { announce('Nothing to harvest.'); return; }
    // simplified growth -> immediate harvest yields
    const cropDef = ITEMS.find(it => it.id===cell.content.id);
    const qty = Math.max(1, Math.floor(1 + Math.random()*3)); const gain = qty * Math.floor(cropDef.price * 1.5);
    setCoins(cn => cn + gain);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type:'empty' }; return nm; });
    announce(`${player.name} harvested ${qty} ${cropDef.name} and earned ₹${gain}`);
  }

  function createListing(itemId, price){ const id = 'L'+Math.random().toString(36).slice(2,8); const listing = { id, seller: player.id, itemId, price, ts: Date.now() }; setMarket(m=> [listing, ...m].slice(0,50)); announce(`${player.name} listed ${itemId} for ₹${price}`); }

  function buyListing(listingId){ const lst = market.find(l => l.id===listingId); if(!lst) return; if(coins < lst.price){ announce('Not enough coins to buy listing.'); return; }
    // transfer
    setCoins(c => c - lst.price);
    setMarket(m => m.filter(x => x.id !== listingId));
    setInventory(inv => ({...inv, [lst.itemId]: (inv[lst.itemId]||0) + 1 }));
    announce(`${player.name} bought ${lst.itemId} for ₹${lst.price}`);
  }

  function quickBuyFromShop(itemId){ const item = ITEMS.find(it => it.id===itemId); if(!item) return; if(coins < item.price){ announce('Not enough coins to buy item.'); return; } setCoins(c => c - item.price); setInventory(inv => ({...inv, [itemId]: (inv[itemId]||0) + 1 })); announce(`${player.name} bought ${item.name} from shop for ₹${item.price}`); }

  function visitRandom(){ // simulate visiting other NPC farms — collect small bonus
    const bonus = Math.floor(Math.random()*40)+10; setCoins(c=> c + bonus); announce(`${player.name} visited a neighbor and earned ₹${bonus} by helping out.`); }

  function changeName(n){ setPlayer(p => ({...p, name: n})); announce(`Player changed name to ${n}`); }

  function nextSeason(){ setSeasonIndex(i => (i+1) % seasons.length); announce(`Season changed to ${seasons[(seasonIndex+1)%seasons.length]}`); }

  // small derived helpers
  const ownedPlots = useMemo(()=> { let c=0; map.forEach(row=>row.forEach(cell=>{ if(cell.owner === player.id) c++; })); return c; }, [map, player]);

  function resetAll(){ if(!window.confirm('Reset TwiWorld?')) return; localStorage.clear(); window.location.reload(); }

  function showHelp(){}

  return (
    <div className="min-h-screen bg-black text-white px-4 lg:px-12 py-4">
          {/* Header */}
          <header className="bg-black text-center mb-2">
            <h1 className="text-xl lg:text-2xl font-bold text-yellow-200 mb-2">TwiWorld</h1>
            <p className="text-sm lg:text-lg mx-auto">
              Where Real Meets Digital
            </p>
            <p className="text-sm lg:text-lg text-yellow-200 mx-auto">
                Digital Twin + Metaverse + Marketplace + Customer Satisfaction + Gen Z Jobs
            </p>
          </header>

          <section className="w-full bg-gray-800 shadow-lg rounded-lg p-6 mb-2">
            <style>{`
              .top{display:flex; gap:4px; align-items:center; justify-content:space-between; flex-wrap:wrap}
              .panel{background:#fff; padding:10px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.06)}
              .map{display:grid; gap:8}
              .plot{width:100%; padding-top:100%; position:relative; border-radius:8px; overflow:hidden}
              .plot-inner{position:absolute; inset:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; border-radius:6px}
              .sidebar{width:320px}
              @media(max-width:980px){ .layout{grid-template-columns:1fr} .sidebar{width:100%} }
            `}</style>

            <div className="mt-2 grid grid-cols-2 lg:grid-cols-6 gap-2 text-center place-items-center">
              <button
                onClick={() => setSelectedPlot(null)}
                className="bg-blue-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-blue-700 transition"
              >
                🏗️ Apply for Metaverse
              </button>

              <button
                onClick={visitRandom}
                className="bg-green-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-green-700 transition"
              >
                🌐 Open Twin
              </button>

              <button
                onClick={nextSeason}
                className="bg-yellow-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-yellow-700 transition"
              >
                📩 Customer Request
              </button>

              <button
                onClick={nextSeason}
                className="bg-indigo-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-indigo-700 transition"
              >
                📊 My Datafeed
              </button>

              <button
                onClick={nextSeason}
                className="bg-pink-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-pink-700 transition"
              >
                📡 My Livefeed
              </button>

              <button
                onClick={nextSeason}
                className="bg-teal-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-teal-700 transition"
              >
                🖼️ My Live Image
              </button>

              <button
                onClick={nextSeason}
                className="bg-purple-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-purple-700 transition"
              >
                🌌 My Metaverse
              </button>

              <button
                onClick={nextSeason}
                className="bg-orange-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-orange-700 transition"
              >
                🛠️ My Services
              </button>

              <button
                onClick={nextSeason}
                className="bg-gray-600 text-white text-xs sm:text-sm px-2 py-2 rounded-md hover:bg-gray-700 transition"
              >
                ℹ️ About
              </button>
            </div>
          </section>
    
          {/* About Section */}
          <section className="w-full bg-gray-800 shadow-lg rounded-lg p-6 mb-2">
            <h2 className="text-lg font-semibold mb-4">About TwiWorld</h2>
            <div className="text-sm space-y-3">
              <p className='text-sm'>
                <strong>What:</strong> TwiWorld is a searchable marketplace-metaverse where anyone can open a verified digital shop 
                (<em>signage → twin → live data feed → eCommerce</em>).
              </p>

              <p className='text-sm'>
                <strong>Why Now:</strong> Digital twin and metaverse retail are growing fast; the gig economy and Gen-Z’s interest in flexible tech work 
                make a jobs-led onboarding strategy viable. 
                <span className="italic text-gray-500">(Source: Grand View Research)</span>
              </p>

              <p className='text-sm'>
                <strong>Uniqueness:</strong> Combines 
                (1) low-friction shop creation (<em>thumbnail + metadata</em>), 
                (2) verified live digital twin evidence (<em>geo/time-stamped images & live video</em>), 
                (3) integrated orderflow, payments & logistics, and 
                (4) a microtask Gen-Z workforce to create/verify/capture twins. 
                This bundle is rare — existing players focus on either virtual worlds 
                (<em>Decentraland, generic metaverses</em>) or 3D scan platforms (<em>Matterport</em>) 
                but not a marketplace with live, location-verified digital twins plus a gig worker on-ramp. 
                <span className="italic text-gray-500">(Source: Practical Ecommerce)</span>
              </p>
            </div>

          </section>
    
          {/* Multi-Camera Feeds */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-2">
            {cameraFeeds.map((cam) => {
              const latest = cam.iotHistory[cam.iotHistory.length - 1];
              return (
                <div key={cam.id} className="bg-gray-800 shadow-lg rounded-lg p-4 flex flex-col">
                  <div className="relative w-full h-48 lg:h-64 bg-black rounded-lg flex items-center justify-center text-white text-xl mb-2">
                    {cam.streaming ? `${cam.name} Live...` : `${cam.name} Placeholder`}
                  </div>
                  <div className="flex justify-between mb-2 gap-4">
                    <button
                      onClick={() => toggleStreaming(cam.id)}
                      className={`px-3 py-1 rounded-lg text-white ${
                        cam.streaming ? "bg-red-600" : "bg-blue-600"
                      }`}
                    >
                      {cam.streaming ? "Stop Streaming" : "Start Streaming"}
                    </button>
                    <button
                      onClick={() => captureNewImage(cam.id)}
                      className="px-3 py-1 rounded-lg bg-green-600 text-white"
                    >
                      Capture Image
                    </button>
                  </div>
                  {/* IoT Metrics */}
                  <div className="grid grid-cols-2 gap-2 bg-gray-700 p-2 rounded-lg text-sm mb-2">
                    <div>
                      <strong>Temp:</strong> {latest.temperature} °C
                    </div>
                    <div>
                      <strong>Humidity:</strong> {latest.humidity} %
                    </div>
                    <div>
                      <strong>Pressure:</strong> {latest.pressure} hPa
                    </div>
                    <div className={`${latest.status === "ALERT" ? "text-red-400" : ""}`}>
                      <strong>Status:</strong> {latest.status}
                    </div>
                    <div className="col-span-2 text-xs text-gray-400">
                      Feed by Think5Whys from Lat: {cam.lat.toFixed(4)} Long: {cam.long.toFixed(4)} | Date: {latest.timestamp.toISOString()}
                    </div>
                  </div>
    
                  {/* IoT Chart */}
                  <div className="bg-gray-900 p-2 rounded-lg">
                    <Line data={getChartData(cam.iotHistory)} options={chartOptions} />
                  </div>
                </div>
              );
            })}
          </section>
    
          {/* Captured Image Gallery */}
          <section className="w-full bg-gray-800 shadow-lg rounded-lg p-6 mb-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-yellow-200 font-semibold">Captured Image Gallery</h2>
              <button
                onClick={() => captureNewImage()}
                className="bg-green-600 text-white px-4 py-2 rounded-lg w-60"
              >
                Capture New Image
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {images.map((img, idx) => (
                <div key={idx} className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src={img.url}
                    alt={`Captured ${idx + 1}`}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-2 bg-gray-900">
                    <p className="text-gray-200 text-sm">
                      Camera ID: {img.cameraId || 0} | Lat: {img.lat.toFixed(4)} Long: {img.long.toFixed(4)}
                    </p>
                    <p className="text-gray-400 text-xs">
                      Date: {img.timestamp.toISOString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
    
          <div style={{padding:16, maxWidth:1200, margin:'0 auto', fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial'}}>
            {helpVisible && (
              <div className='bg-white rounded-lg px-4 py-2 mt-4'>
                <h4 className='text-black'>TwiWorld Help & Tutorial</h4>
                <p>Welcome to the TwiWorld! Here’s how to play:</p>
                <ul style={{paddingLeft:16}}>
                  <li>🟩 Click on a plot to select it.</li>
                  <li>💰 Buy plots you don’t own; manage your coins wisely.</li>
                  <li>🌱 Plant seeds from your inventory, wait or harvest immediately.</li>
                  <li>🪵 Place items like fences or trees to decorate your farm.</li>
                  <li>🏪 Use the Quick Shop or Marketplace to buy/sell items.</li>
                  <li>🧑‍🌾 Visit neighbors to earn small bonuses.</li>
                  <li>🌸 Seasons change—plan crops accordingly.</li>
                  <li>📈 Check your Profile for owned plots and quick actions.</li>
                  <li>💡 Tips: Save coins, grow crops efficiently, and watch your farm thrive!</li>
                </ul>
                <p style={{fontSize:12, color:'#666'}}>Tip: You can toggle this help anytime.</p>
              </div>
            )}

          <div style={{display:'grid', gridTemplateColumns:'1fr', gap:12, marginTop:12}} className="layout">
            <div>
              <div className="panel">
                <h3 className='text-blue-700'>Meaverse Map</h3>
                <div style={{display:'grid', gridTemplateColumns:`repeat(${MAP_COLS}, minmax(0, 1fr))`, gap:8, overflowX:'auto'}} className="map">
                  {map.map((row,r) => row.map((cell,c) => (
                    <div key={`${r}-${c}`} className="plot panel" onClick={() => setSelectedPlot({r,c})}>
                      <div className="plot-inner" style={{background: cell.owner ? (cell.owner===player.id? '#f0fff0':'#fff7e6') : '#fbfbfb'}}>
                        <div style={{fontSize:18}}>{cell.content ? (cell.content.type === 'crop' ? ITEMS.find(it=>it.id===cell.content.id)?.icon : (cell.content.type==='item' ? ITEMS.find(it=>it.id===cell.content.id)?.icon : '🧑‍🌾')) : '🟩'}</div>
                        <div style={{fontSize:11, marginTop:6}}>{cell.owner ? (cell.owner===player.id? 'You': 'Resident') : 'Unowned'}</div>
                        <div style={{position:'absolute', left:6, top:6, fontSize:11, color:'#666'}}>{r+1},{c+1}</div>
                      </div>
                    </div>
                  ))) }
                </div>
              </div>

              <div style={{marginTop:12}} className="panel">
                <h4 className='text-white'>Quick Shop</h4>
                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  {ITEMS.map(it => (
                    <div key={it.id} className="panel" style={{display:'flex', gap:8, alignItems:'center'}}>
                      <div style={{fontSize:20}}>{it.icon}</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700}}>{it.name}</div>
                        <div style={{fontSize:12}}>₹{it.price}</div>
                      </div>
                      <button onClick={() => quickBuyFromShop(it.id)}>Buy</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{marginTop:12}}>
            <div className='bg-white rounded-lg px-4 py-2'>
                <h4 className='text-black'>Plot Actions</h4>
                {selectedPlot ? (
                    <div>
                      <div className="text-black">Selected: <strong>{selectedPlot.r+1},{selectedPlot.c+1}</strong></div>
                      <div style={{marginTop:8}}>
                          {map[selectedPlot.r][selectedPlot.c].owner ? (
                          map[selectedPlot.r][selectedPlot.c].owner === player.id ? (
                              <div style={{display:'grid flex-row', gap:8}}>
                              <button onClick={() => harvest(selectedPlot.r, selectedPlot.c)}>Harvest</button>
                              <div style={{display:'flex flex-row', gap:8, flexWrap:'wrap'}}>
                                  {ITEMS.map(it => <button key={it.id} onClick={() => placeItem(selectedPlot.r, selectedPlot.c, it.id)}>{it.icon} {it.name}</button>)}
                              </div>
                              <div className="text-black" style={{marginTop:8}}>Inventory:</div>
                              <ul className="text-black" style={{paddingLeft:18}}>{Object.keys(inventory).map(k=> <li key={k}>{ITEMS.find(it=>it.id===k)?.name || k}: {inventory[k]}</li>)}</ul>
                              </div>
                          ) : (
                              <div>This plot owned by another resident. You can make an offer in marketplace.</div>
                          )
                          ) : (
                          <div style={{display:'flex', gap:8}}>
                              <button onClick={() => buyPlot(selectedPlot.r, selectedPlot.c)}>Buy Plot (₹{PLOT_BASE_PRICE + (selectedPlot.r+selectedPlot.c)*10})</button>
                          </div>
                          )}
                    </div>
                  </div>
                ) : (
                    <div className='text-black'>Select a plot on the map to see actions.</div>
                )}
              </div>

              <div className='mt-2'>     
                <div className='panel mb-2'>
                  <h4 className='text-black'>Marketplace</h4>
                  <div style={{display:'grid', gap:8}}>
                    <div style={{display:'flex', gap:8}}>
                      <select id="mk_item" defaultValue={ITEMS[0].id} style={{flex:1}}>
                        {ITEMS.map(it=> <option key={it.id} value={it.id}>{it.name}</option>)}
                      </select>
                      <input id="mk_price" defaultValue={20} style={{width:80, height:36}} />
                      <button onClick={() => { const itemId = document.getElementById('mk_item').value; const p = parseInt(document.getElementById('mk_price').value || '0'); createListing(itemId, p); }}>List</button>
                    </div>

                    <div style={{maxHeight:220, overflow:'auto'}}> 
                      {market.length === 0 ? <div className='text-black'>No market listings</div> : market.map(l => (
                        <div key={l.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}} className="panel">
                          <div><strong>{ITEMS.find(it=>it.id===l.itemId)?.name}</strong><div style={{fontSize:12}}>Seller: {l.seller}</div></div>
                          <div style={{textAlign:'right'}}>
                            <div>₹{l.price}</div>
                            <div style={{marginTop:6}}><button className="w-24" onClick={() => buyListing(l.id)}>Buy</button></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              
                <div className='panel mb-2'>  
                  <h4 className='text-black mt-2 mb-2'>Activity Feed</h4>
                  <div style={{maxHeight:220, overflow:'auto'}}>
                    {feed.length === 0 ? <div className='text-black'>No activity yet</div> : feed.map((f,i)=> <div key={i} className="text-black" style={{padding:'6px 0', borderBottom:'1px dashed #eee'}}>{new Date(f.ts).toLocaleTimeString()} — {f.text}</div>)}
                  </div>
                </div>

                <div className='panel'>  
                  <h4 className='text-black mt-2'>Profile</h4>
                  <div style={{display:'flex', gap:8, alignItems:'center'}}>
                    <input className="bg-black text-white h-8 px-2" value={player.name} onChange={e=> changeName(e.target.value)} style={{flex:1}} />
                  </div>
                  <div className='text-black mt-2'>Owned plots: <strong>{ownedPlots}</strong></div>
                  <div className='text-black mt-2'>Quick actions:</div>
                  <div style={{display:'flex', gap:8, marginTop:8}}>
                    <button onClick={() => { setCoins(c=>c+50); announce(`${player.name} claimed daily bonus ₹50`); }}>Daily Bonus</button>
                    <button onClick={() => { setCoins(c=>c-30); announce(`${player.name} donated ₹30 to village fund`); }}>Donate ₹30</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <footer className='bg-gray-400 mt-12'>TwiWorld — a virtual village, shop & trading hub.</footer>
        </div>
        </div>
    
    
    
    
    
  );
}
